import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import type { FriendRequest } from '../../types/social';

interface Props {
  incoming: FriendRequest[];
  outgoing: FriendRequest[];
  loading: boolean;
  onAccept: (id: string) => Promise<{ ok: boolean }>;
  onReject: (id: string) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
  onAcceptedToast: (name: string) => void;
  onAcceptError: (name: string) => void;
}

export function FriendRequestsSection({
  incoming,
  outgoing,
  loading,
  onAccept,
  onReject,
  onCancel,
  onAcceptedToast,
  onAcceptError,
}: Props) {
  const [outgoingOpen, setOutgoingOpen] = useState(false);
  const [busy, setBusy] = useState<Record<string, 'accept' | 'reject' | 'cancel' | undefined>>({});

  if (loading) return null;

  const handleAccept = async (req: FriendRequest) => {
    const name = req.other_display_name || 'Amigo';
    setBusy((b) => ({ ...b, [req.id]: 'accept' }));
    try {
      const result = await onAccept(req.id);
      if (result.ok) {
        onAcceptedToast(name);
      } else {
        onAcceptError(name);
      }
    } finally {
      setBusy((b) => ({ ...b, [req.id]: undefined }));
    }
  };

  const handleReject = async (req: FriendRequest) => {
    setBusy((b) => ({ ...b, [req.id]: 'reject' }));
    await onReject(req.id);
    setBusy((b) => ({ ...b, [req.id]: undefined }));
  };

  const handleCancel = async (req: FriendRequest) => {
    setBusy((b) => ({ ...b, [req.id]: 'cancel' }));
    await onCancel(req.id);
    setBusy((b) => ({ ...b, [req.id]: undefined }));
  };

  return (
    <div className="mb-5 px-1">
      {incoming.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="font-display font-bold text-h3 text-ink-900">Pedidos de amizade</h2>
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-green-700 text-white text-caption font-display font-bold">
              {incoming.length}
            </span>
          </div>
          <div className="space-y-2">
            {incoming.map((req) => (
              <article
                key={req.id}
                className="bg-white border border-ink-300 rounded-xl p-3 flex items-center gap-3 transition-opacity"
              >
                <Avatar name={req.other_display_name || 'Amigo'} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-[14px] text-ink-900 truncate">
                    {req.other_display_name || 'Sem nome'}
                  </p>
                  {req.other_username && (
                    <p className="text-caption text-ink-500 truncate">@{req.other_username}</p>
                  )}
                  <p className="text-caption text-ink-700 mt-0.5">Quer ser seu amigo</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleAccept(req)}
                    disabled={!!busy[req.id]}
                    className="h-8 px-3 rounded-md bg-green-700 text-white font-display font-bold text-[12px] hover:bg-green-900 btn-focus disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                  >
                    {busy[req.id] === 'accept' && (
                      <Loader2 size={12} className="animate-spin" />
                    )}
                    Aceitar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReject(req)}
                    disabled={!!busy[req.id]}
                    className="h-8 px-3 rounded-md border-[1.5px] border-ink-300 text-ink-700 font-display font-bold text-[12px] hover:bg-ink-100/50 btn-focus disabled:opacity-60"
                  >
                    Recusar
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {outgoing.length > 0 && (
        <div className="bg-white border border-ink-300 rounded-xl overflow-hidden mb-3">
          <button
            type="button"
            onClick={() => setOutgoingOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 btn-focus hover:bg-ink-100/30"
            aria-expanded={outgoingOpen}
          >
            <span className="font-display font-bold text-[13px] text-ink-700">
              Pedidos enviados · {outgoing.length}
            </span>
            {outgoingOpen ? (
              <ChevronUp size={16} className="text-ink-500" />
            ) : (
              <ChevronDown size={16} className="text-ink-500" />
            )}
          </button>
          {outgoingOpen && (
            <div className="border-t border-ink-100 divide-y divide-ink-100">
              {outgoing.map((req) => (
                <div key={req.id} className="px-4 py-3 flex items-center gap-3">
                  <Avatar name={req.other_display_name || 'Amigo'} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-[13px] text-ink-900 truncate">
                      {req.other_display_name || 'Sem nome'}
                    </p>
                    {req.other_username && (
                      <p className="text-caption text-ink-500 truncate">@{req.other_username}</p>
                    )}
                    <p className="text-caption text-ink-500 mt-0.5">Aguardando resposta</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCancel(req)}
                    disabled={!!busy[req.id]}
                    aria-label="Cancelar pedido"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 btn-focus disabled:opacity-60"
                  >
                    {busy[req.id] === 'cancel' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <X size={14} strokeWidth={2.25} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
